import { isAppPortocar } from '@/index';
import { usePortoPoliciesList } from '@/core/domain/porto/policies/porto-policies.hooks';
import { Col, Form, Input, Modal, Row, Select, Typography } from 'antd';
import PoliciesCardList from '@/components/_common/PoliciesCardList';
import ModalLoadingOverlay from '@/components/_common/ModalLoadingOverlay';
import { useEffect, useMemo } from 'react';
import { PortoPolicy } from '@/core/domain/porto/policies/porto-policies.types';
import {
  FloatingFormItem,
  InputDecimal,
  InputDate,
} from '@/components/_common';
import {
  WorkOrderComplement,
  WorkOrderComplementTankLevel,
} from '@/core/domain/workorder/WorkOrdersComplements/WorkOrdersComplements.types';
import moment, { Moment } from 'moment';
import { FormattedMessage, useIntl } from 'react-intl';
import { useWorkorderConfigurationCompanyQuery } from '@/core/domain/workorder/WorkordersConfiguration/WorkordersConfiguration.hooks';
import { useDefaultEmptyFieldError, useDisclosure } from '@/hooks';
import useWorkOrderAddPolicy, {
  useWorkOrder,
} from '@/core/domain/workorder/WorkOrders/WorkOrders.hooks';
import { useWorkOrderComplement } from '@/core/domain/workorder/WorkOrdersComplements/WorkOrdersComplements.hooks';
import {
  WorkOrder,
  WorkOrderAddPolicyCompanyCode,
} from '@/core/domain/workorder/WorkOrders/WorkOrders.types';

const Title = Typography.Title;

interface FormValues {
  prisma: string;
  currentKilometers: number;
  tankLevel: WorkOrderComplementTankLevel;
  lastOilChange: Moment;
  lastOilChangeKilometers: number;
  policy?: PortoPolicy;
}

interface Props {
  complement?: WorkOrderComplement;
  workOrder?: WorkOrder;
  refetch?: () => Promise<unknown>;
  onCancel?: VoidFunction;
}

export const useWorkOrderVerifyHasPolicy = (
  props: {
    complementId: number;
    workOrderId: number;
  },
  enabled = false,
) => {
  const { data: workOrder } = useWorkOrder(
    enabled ? props.workOrderId : undefined,
  );
  const {
    data: complement,
    isLoading,
    refetch,
  } = useWorkOrderComplement(enabled ? props.complementId : undefined);

  const policyList = usePortoPoliciesList(
    workOrder?.vehicle?.licensePlate,
    enabled && !complement?.policyNumber && isAppPortocar && !isLoading,
  );

  if (!isAppPortocar || !!complement?.policyNumber || !enabled) return null;

  if (policyList.isLoading || !!policyList.data?.length)
    return {
      workOrder,
      complement,
      refetch,
      isLoading: isLoading || policyList.isLoading,
    };

  return null;
};

export default function WorkOrderVerifyPolicy({
  workOrder,
  complement,
  refetch,
  onCancel,
}: Props) {
  const intl = useIntl();
  const requiredField = useDefaultEmptyFieldError();

  const [form] = Form.useForm<FormValues>();

  const hasFuelEletric = useMemo(() => {
    return workOrder?.vehicle?.fuelName?.toLowerCase()?.includes('elétrico');
  }, [workOrder?.vehicle]);

  const policyForm = Form.useWatch('policy', form);

  const [opened, action] = useDisclosure();

  const policyList = usePortoPoliciesList(
    workOrder?.vehicle?.licensePlate,
    !complement?.policyNumber && isAppPortocar,
  );

  const hasPolicy = !!policyList.data?.length;

  const { data: configWorkOrder } = useWorkorderConfigurationCompanyQuery(
    workOrder?.company.id,
    isAppPortocar,
  );

  const workOrderAddPolicy = useWorkOrderAddPolicy();

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      if (!workOrder || !complement || !values.policy) return;

      await workOrderAddPolicy.mutateAsync({
        companyCode: WorkOrderAddPolicyCompanyCode[values.policy.companyCode],
        complementId: complement.id,
        lastOilChange: values.lastOilChange.format(),
        lastOilChangeKilometers: values.lastOilChangeKilometers,
        policyNumber: values.policy.policyNumber,
        vehicle: {
          ...values.policy.vehicle,
          licensePlate: workOrder.vehicle.licensePlate,
          currentKilometers: values.currentKilometers,
          prisma: values.prisma,
          tankLevel: values.tankLevel,
        },
        vehicleId: workOrder.vehicle.id,
      });

      policyList.remove();
      await refetch?.();
    } catch {}
  };

  const handleClose = () => {
    form.resetFields();
    action.close();
    onCancel?.();
  };

  useEffect(() => {
    if (complement && !complement.policyNumber && hasPolicy) {
      form.setFieldsValue({
        currentKilometers: complement.currentKilometers,
        lastOilChange: complement.lastOilChange
          ? moment(complement.lastOilChange)
          : undefined,
        lastOilChangeKilometers: complement.lastOilChangeKilometers,
        prisma: complement.prisma,
        tankLevel: complement.tankLevel,
      });

      action.open();
    }
  }, [complement?.policyNumber, hasPolicy]);

  if (!isAppPortocar) return null;

  if (policyList.isLoading && !complement?.policyNumber)
    return (
      <ModalLoadingOverlay
        messages={['Verificando se o veículo possui apólice']}
      />
    );

  if (!hasPolicy) return null;

  return (
    <Modal
      title="Selecione a apolice do veículo"
      open={opened}
      closable={false}
      width={600}
      okText="Vincular apólice"
      onOk={handleSubmit}
      confirmLoading={workOrderAddPolicy.isLoading}
      cancelButtonProps={{ onClick: handleClose }}
      maskClosable={false}
    >
      <Form form={form}>
        <>
          <Form.Item
            hidden
            name="policy"
            rules={[{ required: true, message: 'Selecione uma apólice' }]}
          />

          <Row gutter={[16, 8]}>
            <Col span={24}>
              <Form.Item shouldUpdate>
                {({ getFieldError }) => {
                  const error = getFieldError('policy');

                  return (
                    <>
                      <PoliciesCardList
                        loading={policyList.isLoading}
                        onSelect={(value) => {
                          if (workOrderAddPolicy.isLoading) return;
                          form.setFieldValue('policy', value);
                        }}
                        licensePlate={workOrder?.vehicle?.licensePlate}
                        policies={policyList.data}
                        policyNumber={policyForm?.policyNumber}
                      />

                      {!!error && (
                        <span style={{ color: 'red', fontSize: 11.2 }}>
                          {error}
                        </span>
                      )}
                    </>
                  );
                }}
              </Form.Item>
            </Col>

            <Col span={24}>
              <Title level={4}>
                <FormattedMessage id="workOrders.domain.vehicle" />
              </Title>
            </Col>

            <Col span={8}>
              <FloatingFormItem
                name="prisma"
                label={intl.formatMessage({
                  id: 'workOrders.domain.complements.prisma',
                })}
                rules={[
                  {
                    required: configWorkOrder?.prisma,
                    message: intl.formatMessage({
                      id: 'workOrders.fieldRequiredByConfigCompany',
                    }),
                  },
                ]}
              >
                <Input disabled={workOrderAddPolicy.isLoading} />
              </FloatingFormItem>
            </Col>

            <Col span={8}>
              <FloatingFormItem
                name="currentKilometers"
                label={intl.formatMessage({
                  id: 'workOrders.domain.complements.currentKilometers',
                })}
                rules={[
                  {
                    required: isAppPortocar || configWorkOrder?.isKmRequired,
                    message: intl.formatMessage({
                      id: 'workOrders.fieldRequiredByConfigCompany',
                    }),
                  },
                ]}
              >
                <InputDecimal
                  fractions={0}
                  min={0}
                  max={5000000}
                  disabled={workOrderAddPolicy.isLoading}
                />
              </FloatingFormItem>
            </Col>

            <Col span={8}>
              <FloatingFormItem
                name="tankLevel"
                label={intl.formatMessage({
                  id: 'workOrders.domain.complements.tankLevel',
                })}
                rules={[
                  {
                    required: configWorkOrder?.tankLevel,
                    message: intl.formatMessage({
                      id: 'workOrders.fieldRequiredByConfigCompany',
                    }),
                  },
                ]}
              >
                <Select
                  allowClear
                  style={{ width: '100%' }}
                  disabled={workOrderAddPolicy.isLoading}
                  options={Object.values(WorkOrderComplementTankLevel).map(
                    (a) => ({
                      label: intl.formatMessage({
                        id: `workOrders.${a}`,
                      }),
                      value: a,
                    }),
                  )}
                />
              </FloatingFormItem>
            </Col>

            <Col span={12}>
              <FloatingFormItem
                name="lastOilChange"
                label={intl.formatMessage({
                  id: 'workOrders.domain.complements.lastOilChange',
                })}
                rules={hasFuelEletric ? [] : [requiredField]}
              >
                <InputDate
                  disabledDate={(date) => date.isAfter(moment(), 'day')}
                  disabled={workOrderAddPolicy.isLoading}
                />
              </FloatingFormItem>
            </Col>

            <Col span={12}>
              <FloatingFormItem
                name="lastOilChangeKilometers"
                label={intl.formatMessage({
                  id: 'workOrders.domain.complements.lastOilChangeKilometers',
                })}
                rules={hasFuelEletric ? [] : [requiredField]}
              >
                <InputDecimal
                  fractions={0}
                  min={0}
                  disabled={workOrderAddPolicy.isLoading}
                />
              </FloatingFormItem>
            </Col>
          </Row>
        </>
      </Form>
    </Modal>
  );
}
